// Bot configuration data for the operator console. The bot is
// six independently-configurable dimensions (architecture/bot-composition-model);
// the page composes the design-system dimension editors around this shape.
//
// Wired: getBotConfig() -> GET
// /admin/tenants/:tenantId/bots/:botId/config, the flat console projection of a
// bot's merged BotConfig + skills (each with its flow tree) + KB assets. Fields
// with no Phase-1 source (the out-of-hours message) come
// back empty. saveBotConfig() stays a stub: the per-dimension write path goes
// through the existing PATCH /admin/bots/:id/:dimension + publish endpoints; a
// dedicated console save is a tracked follow-up (handoff-backend-to-design.md).

import { apiClient } from '../../api/client.js'
import type {
  DaySchedule,
  FlowEdgeData,
  FlowNodeData,
  KBSource,
  SkillStatus,
  ToneValue,
} from '../../design-system/index.js'

export interface BotConfigSkill {
  id: string
  name: string
  intent: string
  status: SkillStatus
  steps: number
  meta?: string
}

export interface BotConfig {
  botName: string
  intent: string
  // Personalidad
  tone: ToneValue
  /** Where the formality comes from. 'perfil' = DERIVED from the client's
   *  tú/usted answer in the Perfil del negocio, so the operator radio only
   *  reports it; 'operador' = the bot has no profile and the radio still writes. */
  toneSource?: 'perfil' | 'operador'
  neverSay: string[]
  // Reglas
  rules: { id: string; title: string; detail: string; locked: boolean; enabled: boolean }[]
  handoffPhrases: string[]
  /** What happens to a conversation handed off to a human that nobody picks up. */
  handoffRecovery: { mode: 'auto_return' | 'sticky'; autoReturnAfterMinutes: number }
  // Conocimiento
  sources: KBSource[]
  /** What the bot does when no knowledge matches: hand off to a human, or let
   *  the LLM answer from personality. Decides whether a KB-less bot replies. */
  onNoMatch: 'handoff' | 'generic_fallback'
  // Horarios
  week: DaySchedule[]
  // NO `exceptions` (C3): the console rendered a list with a working remove
  // handler over an array the projection hardcoded to [] and no write path ever
  // filled. Removed rather than disabled — honest absence beats a dead door.
  outOfHoursMessage: string
  proactiveDays: number[]
  // Avanzado
  model: string
  temperature: number
  sandbox: boolean
  /** Skills master switch — off means plain AI + KB, no flow dispatch. */
  habilidadesEnabled: boolean
  /** Per-skill-walk USD ceiling before a forced handoff. */
  skillCostCeilingUsd: number
  // Habilidades
  skills: BotConfigSkill[]
  flow: { nodes: FlowNodeData[]; edges: FlowEdgeData[] }
}

/**
 * GET /admin/tenants/:t/bots/:b/config — the flat console projection of a bot's
 * merged BotConfig + skills + KB assets. Lossy by design; fields with no
 * Phase-1 source come back empty.
 */
export async function getBotConfig(tenantId: string, botId: string): Promise<BotConfig> {
  const res = await apiClient.get<{ data: BotConfig }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/bots/${encodeURIComponent(botId)}/config`,
  )
  return res.data.data
}

/**
 * PATCH /admin/tenants/:t/bots/:b/config — persists only the round-trippable
 * Personalidad/Avanzado/Horarios fields (server does read-merge-write + cache
 * invalidation). Structurally-lossy dimensions (rules, KB) have their own flows.
 */
export async function saveBotConfig(
  tenantId: string,
  botId: string,
  config: BotConfig,
): Promise<void> {
  // Persists the cleanly round-trippable Personalidad + Avanzado fields via
  // PATCH .../config (server-side read-merge-write + cache invalidation). The
  // structurally-lossy dimensions (schedule, rules) keep their own flows.
  await apiClient.patch(
    `/admin/tenants/${encodeURIComponent(tenantId)}/bots/${encodeURIComponent(botId)}/config`,
    {
      // A profile-bearing bot derives its tono from the client's tú/usted
      // answer; the console shows it read-only, so it must not write it back.
      ...(config.toneSource === 'perfil' ? {} : { tone: config.tone }),
      neverSay: config.neverSay,
      temperature: config.temperature,
      sandbox: config.sandbox,
      habilidadesEnabled: config.habilidadesEnabled,
      skillCostCeilingUsd: config.skillCostCeilingUsd,
      model: config.model === 'haiku' ? 'haiku' : 'sonnet',
      proactiveDays: config.proactiveDays,
      handoffPhrases: config.handoffPhrases,
      week: config.week,
      outOfHoursMessage: config.outOfHoursMessage,
      handoffRecovery: config.handoffRecovery,
    },
  )
}

/**
 * Set the bot's no-match behaviour (handoff vs generic_fallback). The first
 * live console config write (F-L3): goes to PATCH .../no-match, which updates
 * the bot + invalidates the config cache so it applies on the next inbound.
 */
export async function saveNoMatch(
  tenantId: string,
  botId: string,
  onNoMatch: BotConfig['onNoMatch'],
): Promise<void> {
  await apiClient.patch(
    `/admin/tenants/${encodeURIComponent(tenantId)}/bots/${encodeURIComponent(botId)}/no-match`,
    { onNoMatch },
  )
}

// ─── Knowledge base (operator KB endpoints) ──────────────────────────────────

export interface KbSource {
  id: string
  kind: string
  sourceUrl: string | null
  filename: string | null
  sizeBytes: number | null
  createdAt: string
  chunks: number
  /** Ingestion-job status: queued/parsing/chunking/embedding/review/active/failed/… */
  status: string
  error: string | null
}

const kbBase = (tenantId: string, botId: string): string =>
  `/admin/tenants/${encodeURIComponent(tenantId)}/bots/${encodeURIComponent(botId)}/kb`

/** GET /admin/tenants/:t/bots/:b/kb — the bot's ingested sources with per-job status. */
export async function listKbSources(tenantId: string, botId: string): Promise<KbSource[]> {
  const res = await apiClient.get<{ data: KbSource[] }>(kbBase(tenantId, botId))
  return res.data.data
}

/** POST /admin/tenants/:t/bots/:b/kb with `kind: 'url'` — queues a URL for ingestion. */
export async function uploadKbUrl(tenantId: string, botId: string, url: string): Promise<void> {
  await apiClient.post(kbBase(tenantId, botId), { kind: 'url', source_url: url })
}

/** POST /admin/tenants/:t/bots/:b/kb as multipart — uploads a document for ingestion. */
export async function uploadKbFile(tenantId: string, botId: string, file: File): Promise<void> {
  const form = new FormData()
  form.append('file', file)
  // Let the browser set the multipart boundary; axios passes FormData through.
  await apiClient.post(kbBase(tenantId, botId), form)
}

/** Approve a source's latest ingestion job (review → active) so the bot uses it. */
export async function approveKbSource(
  tenantId: string,
  botId: string,
  assetId: string,
): Promise<void> {
  await apiClient.post(`${kbBase(tenantId, botId)}/${encodeURIComponent(assetId)}/approve`, {})
}

/** DELETE /admin/tenants/:t/bots/:b/kb/:assetId — removes a source and its fragments. */
export async function deleteKbSource(
  tenantId: string,
  botId: string,
  assetId: string,
): Promise<void> {
  await apiClient.delete(`${kbBase(tenantId, botId)}/${encodeURIComponent(assetId)}`)
}

/** The KB operations the `KbPanel` needs, already bound to one bot. Lets the
 *  same panel serve the operator console (cross-tenant routes) and the owner
 *  editor (session-scoped /workers routes) without knowing which it is. */
/** One ingested fragment of a source (a chunk), for the "view fragments" UI. */
export interface KbChunk {
  id: string
  title: string | null
  content: string
  position: number
}

/** A retrieval match for the "probar conocimiento" probe (with its score). */
export interface KbMatch {
  id: string
  title: string
  content: string
  category: string
  similarity: number
}
export interface KbPreviewResult {
  query: string
  minSimilarity: number
  matches: KbMatch[]
  /** The next-best fragment BELOW the threshold (to tune it), or null. */
  excluded: KbMatch | null
  /** Present only when the probe could not RUN (embed provider down). Empty
   *  `matches` then means "we could not look", not "you have no content" — the
   *  panel must not tell the author to upload a document over an outage. */
  degraded?: { reason: 'embeddings_unavailable'; message: string }
}

/** A question the agent couldn't answer (failure visibility). */
export interface UnansweredQuestion {
  question: string
  /** How many times it was asked. */
  times: number
  /** ISO timestamp of the most recent time. */
  lastAt: string
  /** Title of the closest (rejected) fragment, or null when nothing was near. */
  closest: string | null
  /** Whether it's a real content gap (`on_topic`) or off-topic noise
   *  (`off_topic`). The panel surfaces gaps and tucks noise away. */
  relevance: 'on_topic' | 'off_topic'
}

export interface KbApi {
  list: () => Promise<KbSource[]>
  uploadUrl: (url: string) => Promise<void>
  uploadFile: (file: File) => Promise<void>
  approve: (assetId: string) => Promise<void>
  remove: (assetId: string) => Promise<void>
  /** List a source's ingested fragments. */
  chunks?: (assetId: string) => Promise<KbChunk[]>
  /** "Probar conocimiento": what would the bot retrieve for this question? */
  preview?: (query: string) => Promise<KbPreviewResult>
  /** Questions the agent recently couldn't answer. */
  unanswered?: () => Promise<UnansweredQuestion[]>
}

/** Operator-console KB api (the `/admin/tenants/:t/bots/:b/kb` routes). */
export function operatorKbApi(tenantId: string, botId: string): KbApi {
  return {
    list: () => listKbSources(tenantId, botId),
    uploadUrl: (url) => uploadKbUrl(tenantId, botId, url),
    uploadFile: (file) => uploadKbFile(tenantId, botId, file),
    approve: (assetId) => approveKbSource(tenantId, botId, assetId),
    remove: (assetId) => deleteKbSource(tenantId, botId, assetId),
    // B2: the diagnosis trio, same panels the owner gets — KbPanel renders
    // them purely off presence.
    chunks: async (assetId) =>
      (
        await apiClient.get<{ data: KbChunk[] }>(
          `${kbBase(tenantId, botId)}/${encodeURIComponent(assetId)}/chunks`,
        )
      ).data.data,
    preview: async (query) =>
      (
        await apiClient.post<{ data: KbPreviewResult }>(`${kbBase(tenantId, botId)}/preview`, {
          query,
        })
      ).data.data,
    unanswered: async () =>
      (await apiClient.get<{ data: UnansweredQuestion[] }>(`${kbBase(tenantId, botId)}/unanswered`))
        .data.data,
  }
}
